import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CalendarCheck, Plus, Search, Edit, Trash2, Eye, ChevronLeft, Clock, MapPin,
  DollarSign, Users, Calendar, Check, X, Loader2, BarChart3, Image as ImageIcon,
  Car, AlertCircle, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const MODES = [
  { id: 'daily', label: 'Harian', unit: 'per hari', icon: Calendar, desc: 'Rental mobil, motor, villa, hotel' },
  { id: 'hourly', label: 'Per Jam', unit: 'per jam', icon: Clock, desc: 'Salon, lapangan, studio' },
  { id: 'session', label: 'Per Sesi', unit: 'per sesi', icon: CalendarCheck, desc: 'Dokter, konsultasi' },
  { id: 'quantity', label: 'Per Unit', unit: 'per unit', icon: Users, desc: 'Meja restoran, kursi' },
];

const STATUS_MAP = {
  pending_payment: { label: 'Menunggu Bayar', color: 'bg-yellow-100 text-yellow-800' },
  awaiting_payment: { label: 'Menunggu Transfer', color: 'bg-orange-100 text-orange-800' },
  confirmed: { label: 'Dikonfirmasi', color: 'bg-green-100 text-green-800' },
  completed: { label: 'Selesai', color: 'bg-blue-100 text-blue-800' },
  cancelled: { label: 'Dibatalkan', color: 'bg-red-100 text-red-800' },
};

const defaultSvcForm = {
  name: '', description: '', booking_mode: 'daily', price: 0, duration_minutes: 60,
  capacity: 1, images: [], location: '', location_url: '', terms: '', cancellation_policy: '',
  operating_hours: '', category: '', is_active: true,
};

const defaultSlotForm = { date: '', time_start: '', time_end: '', capacity: 1, price_override: '', is_blocked: false, label: '' };

export default function TenantBookings() {
  // State
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainTab, setMainTab] = useState('services');

  // Service form
  const [showSvcForm, setShowSvcForm] = useState(false);
  const [svcForm, setSvcForm] = useState(defaultSvcForm);
  const [editSvcId, setEditSvcId] = useState(null);
  const [savingSvc, setSavingSvc] = useState(false);

  // Service detail
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [svcSlots, setSvcSlots] = useState([]);
  const [svcBookings, setSvcBookings] = useState([]);

  // Slot form
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [slotForm, setSlotForm] = useState(defaultSlotForm);

  // Booking detail
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusAction, setStatusAction] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');

  // Fetch
  const fetchServices = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/booking-services');
      setServices(data.services || []);
    } catch { toast.error('Gagal memuat layanan'); }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterService) params.service_id = filterService;
      const { data } = await api.get('/tenant/bookings', { params });
      setBookings(data.bookings || []);
    } catch { toast.error('Gagal memuat booking'); }
  }, [search, filterStatus, filterService]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/bookings/stats');
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchServices(), fetchBookings(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchServices, fetchBookings, fetchStats]);

  // Service CRUD
  const openCreateSvc = () => {
    setSvcForm(defaultSvcForm);
    setEditSvcId(null);
    setShowSvcForm(true);
  };

  const openEditSvc = (svc) => {
    setSvcForm({
      name: svc.name || '', description: svc.description || '', booking_mode: svc.booking_mode || 'daily',
      price: svc.price || 0, duration_minutes: svc.duration_minutes || 60, capacity: svc.capacity || 1,
      images: svc.images || [], location: svc.location || '', location_url: svc.location_url || '',
      terms: svc.terms || '', cancellation_policy: svc.cancellation_policy || '',
      operating_hours: svc.operating_hours || '', category: svc.category || '', is_active: svc.is_active !== false,
    });
    setEditSvcId(svc.id);
    setShowSvcForm(true);
  };

  const saveSvc = async () => {
    if (!svcForm.name) return toast.error('Nama layanan wajib diisi');
    setSavingSvc(true);
    try {
      if (editSvcId) {
        await api.put(`/tenant/booking-services/${editSvcId}`, svcForm);
        toast.success('Layanan diperbarui');
      } else {
        await api.post('/tenant/booking-services', svcForm);
        toast.success('Layanan dibuat');
      }
      setShowSvcForm(false);
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    }
    setSavingSvc(false);
  };

  const deleteSvc = async (id) => {
    if (!window.confirm('Hapus layanan ini?')) return;
    try {
      await api.delete(`/tenant/booking-services/${id}`);
      toast.success('Layanan dihapus');
      fetchServices();
      if (selectedSvc?.id === id) setSelectedSvc(null);
    } catch { toast.error('Gagal menghapus'); }
  };

  // Service detail
  const openSvcDetail = async (svc) => {
    setSelectedSvc(svc);
    setDetailTab('info');
    fetchSvcSlots(svc.id);
    fetchSvcBookings(svc.id);
  };

  const fetchSvcSlots = async (sid) => {
    try {
      const { data } = await api.get(`/tenant/booking-services/${sid}/slots`);
      setSvcSlots(data.slots || []);
    } catch {}
  };

  const fetchSvcBookings = async (sid) => {
    try {
      const { data } = await api.get('/tenant/bookings', { params: { service_id: sid, limit: 100 } });
      setSvcBookings(data.bookings || []);
    } catch {}
  };

  // Slot management
  const saveSlot = async () => {
    if (!slotForm.date) return toast.error('Tanggal wajib diisi');
    try {
      await api.post(`/tenant/booking-services/${selectedSvc.id}/slots`, {
        slots: [{ ...slotForm, price_override: slotForm.price_override ? parseInt(slotForm.price_override) : null }]
      });
      toast.success('Slot disimpan');
      setShowSlotForm(false);
      fetchSvcSlots(selectedSvc.id);
    } catch { toast.error('Gagal menyimpan slot'); }
  };

  const deleteSlot = async (slotId) => {
    try {
      await api.delete(`/tenant/booking-services/${selectedSvc.id}/slots/${slotId}`);
      toast.success('Slot dihapus');
      fetchSvcSlots(selectedSvc.id);
    } catch { toast.error('Gagal menghapus slot'); }
  };

  // Booking status update
  const updateBookingStatus = async () => {
    if (!statusAction || !selectedBooking) return;
    setUpdatingStatus(true);
    try {
      await api.put(`/tenant/bookings/${selectedBooking.id}/status`, {
        status: statusAction,
        cancel_reason: cancelReason.trim(),
      });
      toast.success(`Booking ${statusAction === 'confirmed' ? 'dikonfirmasi' : statusAction === 'completed' ? 'diselesaikan' : 'dibatalkan'}`);
      setSelectedBooking(null);
      setStatusAction('');
      setCancelReason('');
      fetchBookings();
      fetchStats();
      if (selectedSvc) fetchSvcBookings(selectedSvc.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal update status');
    }
    setUpdatingStatus(false);
  };

  const fmt = (n) => `Rp ${(n || 0).toLocaleString('id-ID')}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const getModeLabel = (m) => MODES.find(x => x.id === m)?.label || m;

  // ========================
  // RENDER
  // ========================

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-blue-500" size={32} />
    </div>
  );

  // Booking detail modal
  const BookingDetailModal = () => (
    <Dialog open={!!selectedBooking} onOpenChange={() => { setSelectedBooking(null); setStatusAction(''); setCancelReason(''); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Detail Booking</DialogTitle></DialogHeader>
        {selectedBooking && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">No. Booking</span><span className="font-bold">{selectedBooking.booking_number}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Layanan</span><span className="font-medium">{selectedBooking.service_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tanggal</span><span>{selectedBooking.time_info || fmtDate(selectedBooking.booking_date)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Durasi</span><span>{selectedBooking.duration_label}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-bold text-green-600">{fmt(selectedBooking.total_price)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Status</span>
                <Badge className={STATUS_MAP[selectedBooking.status]?.color || 'bg-slate-100'}>{STATUS_MAP[selectedBooking.status]?.label || selectedBooking.status}</Badge>
              </div>
              <div className="flex justify-between"><span className="text-slate-500">Pembayaran</span><span>{selectedBooking.payment_status}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Metode</span><span>{selectedBooking.payment_method}</span></div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 space-y-1 text-sm">
              <p className="font-semibold text-blue-800">Customer</p>
              <p>{selectedBooking.customer_name}</p>
              <p className="text-slate-500">{selectedBooking.customer_email}</p>
              {selectedBooking.customer_phone && <p className="text-slate-500">{selectedBooking.customer_phone}</p>}
              {selectedBooking.notes && <p className="mt-2 text-slate-600 italic">Catatan: {selectedBooking.notes}</p>}
            </div>

            {/* Status actions */}
            {['pending_payment', 'awaiting_payment'].includes(selectedBooking.status) && (
              <div className="flex gap-2">
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => setStatusAction('confirmed')}>
                  <Check size={14} className="mr-1" /> Konfirmasi Bayar
                </Button>
                <Button variant="destructive" className="flex-1" onClick={() => setStatusAction('cancelled')}>
                  <X size={14} className="mr-1" /> Batalkan
                </Button>
              </div>
            )}
            {selectedBooking.status === 'confirmed' && (
              <div className="flex gap-2">
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => setStatusAction('completed')}>
                  <Check size={14} className="mr-1" /> Selesaikan
                </Button>
                <Button variant="destructive" className="flex-1" onClick={() => setStatusAction('cancelled')}>
                  <X size={14} className="mr-1" /> Batalkan
                </Button>
              </div>
            )}

            {statusAction && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-yellow-800">
                  {statusAction === 'confirmed' ? 'Konfirmasi booking ini?' : statusAction === 'completed' ? 'Selesaikan booking ini?' : 'Batalkan booking ini?'}
                </p>
                {statusAction === 'cancelled' && (
                  <Input placeholder="Alasan pembatalan (opsional)" value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={updateBookingStatus} disabled={updatingStatus}>
                    {updatingStatus && <Loader2 size={14} className="animate-spin mr-1" />} Ya, Lanjutkan
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setStatusAction(''); setCancelReason(''); }}>Batal</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  // Service detail view
  if (selectedSvc) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedSvc(null)}><ChevronLeft size={16} /> Kembali</Button>
          <div>
            <h2 className="text-xl font-bold">{selectedSvc.name}</h2>
            <p className="text-sm text-slate-500">{getModeLabel(selectedSvc.booking_mode)} · {fmt(selectedSvc.price)} {selectedSvc.price_unit}</p>
          </div>
        </div>

        <Tabs value={detailTab} onValueChange={setDetailTab}>
          <TabsList>
            <TabsTrigger value="info">Info</TabsTrigger>
            <TabsTrigger value="slots">Ketersediaan ({svcSlots.length})</TabsTrigger>
            <TabsTrigger value="bookings">Booking ({svcBookings.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="info">
            <Card>
              <CardContent className="p-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="text-slate-500">Mode</span><p className="font-medium">{getModeLabel(selectedSvc.booking_mode)}</p></div>
                  <div><span className="text-slate-500">Harga</span><p className="font-medium">{fmt(selectedSvc.price)} {selectedSvc.price_unit}</p></div>
                  <div><span className="text-slate-500">Kapasitas</span><p className="font-medium">{selectedSvc.capacity}</p></div>
                  <div><span className="text-slate-500">Durasi (menit)</span><p className="font-medium">{selectedSvc.duration_minutes}</p></div>
                  <div><span className="text-slate-500">Lokasi</span><p className="font-medium">{selectedSvc.location || '-'}</p></div>
                  <div><span className="text-slate-500">Jam Operasi</span><p className="font-medium">{selectedSvc.operating_hours || '-'}</p></div>
                  <div><span className="text-slate-500">Status</span><Badge className={selectedSvc.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{selectedSvc.is_active ? 'Aktif' : 'Nonaktif'}</Badge></div>
                  <div><span className="text-slate-500">Total Booking</span><p className="font-medium">{selectedSvc.total_bookings || 0}</p></div>
                </div>
                {selectedSvc.description && <div><span className="text-slate-500">Deskripsi</span><p className="mt-1">{selectedSvc.description}</p></div>}
                {selectedSvc.terms && <div><span className="text-slate-500">Syarat & Ketentuan</span><p className="mt-1 whitespace-pre-wrap">{selectedSvc.terms}</p></div>}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => openEditSvc(selectedSvc)}><Edit size={14} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteSvc(selectedSvc.id)}><Trash2 size={14} className="mr-1" /> Hapus</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="slots">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitle className="text-base">Ketersediaan / Slot</CardTitle>
                <Button size="sm" onClick={() => { setSlotForm(defaultSlotForm); setShowSlotForm(true); }}>
                  <Plus size={14} className="mr-1" /> Tambah Slot
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                {svcSlots.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Belum ada slot. Tambahkan slot ketersediaan untuk layanan ini.
                    {selectedSvc.booking_mode === 'daily' && <><br/>Untuk mode harian, slot opsional — hanya tambahkan jika ingin block tanggal atau override harga.</>}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {svcSlots.map(s => (
                      <div key={s.id} className={`flex items-center justify-between p-3 rounded-lg border ${s.is_blocked ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
                        <div>
                          <p className="font-medium text-sm">{fmtDate(s.date)} {s.time_start && `${s.time_start}-${s.time_end}`}</p>
                          <p className="text-xs text-slate-500">
                            {s.is_blocked ? 'DIBLOKIR' : `Kapasitas: ${s.capacity}`}
                            {s.price_override != null && ` · Harga: ${fmt(s.price_override)}`}
                            {s.label && ` · ${s.label}`}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => deleteSlot(s.id)}><Trash2 size={14} /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bookings">
            <Card>
              <CardContent className="p-4">
                {svcBookings.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">Belum ada booking untuk layanan ini.</p>
                ) : (
                  <div className="space-y-2">
                    {svcBookings.map(b => (
                      <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedBooking(b)}>
                        <div>
                          <p className="font-medium text-sm">{b.customer_name} <span className="text-slate-400 font-normal">#{b.booking_number}</span></p>
                          <p className="text-xs text-slate-500">{b.time_info || fmtDate(b.booking_date)} · {b.duration_label}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={`text-xs ${STATUS_MAP[b.status]?.color || 'bg-slate-100'}`}>{STATUS_MAP[b.status]?.label || b.status}</Badge>
                          <p className="text-sm font-medium mt-1">{fmt(b.total_price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Slot form dialog */}
        <Dialog open={showSlotForm} onOpenChange={setShowSlotForm}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Tambah Slot</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Tanggal *</Label><Input type="date" value={slotForm.date} onChange={e => setSlotForm(f => ({ ...f, date: e.target.value }))} /></div>
              {['hourly', 'session'].includes(selectedSvc.booking_mode) && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Jam Mulai</Label><Input type="time" value={slotForm.time_start} onChange={e => setSlotForm(f => ({ ...f, time_start: e.target.value }))} /></div>
                  <div><Label>Jam Selesai</Label><Input type="time" value={slotForm.time_end} onChange={e => setSlotForm(f => ({ ...f, time_end: e.target.value }))} /></div>
                </div>
              )}
              <div><Label>Kapasitas</Label><Input type="number" min={0} value={slotForm.capacity} onChange={e => setSlotForm(f => ({ ...f, capacity: parseInt(e.target.value) || 0 }))} /></div>
              <div><Label>Override Harga (opsional)</Label><Input type="number" placeholder="Kosongkan untuk harga default" value={slotForm.price_override} onChange={e => setSlotForm(f => ({ ...f, price_override: e.target.value }))} /></div>
              <div><Label>Label (opsional)</Label><Input placeholder="e.g. Hari Libur" value={slotForm.label} onChange={e => setSlotForm(f => ({ ...f, label: e.target.value }))} /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="slot-blocked" checked={slotForm.is_blocked} onChange={e => setSlotForm(f => ({ ...f, is_blocked: e.target.checked }))} />
                <Label htmlFor="slot-blocked">Blokir tanggal ini (tidak tersedia)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSlotForm(false)}>Batal</Button>
              <Button onClick={saveSlot}>Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <BookingDetailModal />
      </div>
    );
  }

  // Main view
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarCheck size={24} className="text-blue-600" />
          <div>
            <h1 className="text-xl font-bold">Booking</h1>
            <p className="text-sm text-slate-500">Kelola layanan booking & reservasi</p>
          </div>
        </div>
        <Button onClick={openCreateSvc} className="gap-2"><Plus size={16} /> Layanan Baru</Button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p><p className="text-xs text-slate-500">Total Booking</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p><p className="text-xs text-slate-500">Dikonfirmasi</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{fmt(stats.total_revenue)}</p><p className="text-xs text-slate-500">Total Revenue</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.upcoming}</p><p className="text-xs text-slate-500">Upcoming</p>
          </CardContent></Card>
        </div>
      )}

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="services">Layanan ({services.length})</TabsTrigger>
          <TabsTrigger value="bookings">Semua Booking ({bookings.length})</TabsTrigger>
          <TabsTrigger value="stats">Statistik</TabsTrigger>
        </TabsList>

        {/* SERVICES TAB */}
        <TabsContent value="services">
          {services.length === 0 ? (
            <Card><CardContent className="p-12 text-center">
              <Car size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="font-semibold text-lg mb-2">Belum Ada Layanan</h3>
              <p className="text-slate-500 mb-4">Buat layanan booking pertama Anda — rental mobil, lapangan, salon, dll.</p>
              <Button onClick={openCreateSvc}><Plus size={16} className="mr-1" /> Buat Layanan</Button>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map(svc => (
                <Card key={svc.id} className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => openSvcDetail(svc)}>
                  <div className="relative h-40 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                    {svc.images?.[0] ? (
                      <img src={svc.images[0]} alt={svc.name} className="w-full h-full object-cover" />
                    ) : (
                      <Car size={40} className="text-blue-300" />
                    )}
                    <Badge className="absolute top-2 left-2 text-xs">{getModeLabel(svc.booking_mode)}</Badge>
                    {!svc.is_active && <Badge className="absolute top-2 right-2 bg-red-500 text-white text-xs">Nonaktif</Badge>}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold line-clamp-1">{svc.name}</h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                      {svc.location && <span className="flex items-center gap-1"><MapPin size={12} />{svc.location}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-lg font-bold text-blue-600">{fmt(svc.price)}<span className="text-xs font-normal text-slate-400 ml-1">{svc.price_unit}</span></span>
                      <span className="text-xs text-slate-400">{svc.total_bookings || 0} booking</span>
                    </div>
                    <div className="flex gap-1 mt-3" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditSvc(svc)}><Edit size={12} className="mr-1" /> Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteSvc(svc.id)}><Trash2 size={12} /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* BOOKINGS TAB */}
        <TabsContent value="bookings">
          <div className="flex flex-col md:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Cari nama, email, nomor booking..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchBookings()} />
            </div>
            <Select value={filterStatus} onValueChange={v => { setFilterStatus(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Semua Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterService} onValueChange={v => { setFilterService(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Semua Layanan" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Layanan</SelectItem>
                {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchBookings}><RefreshCw size={14} /></Button>
          </div>

          {bookings.length === 0 ? (
            <Card><CardContent className="p-12 text-center">
              <CalendarCheck size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Belum ada booking.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {bookings.map(b => (
                <Card key={b.id} className="hover:shadow-sm cursor-pointer" onClick={() => setSelectedBooking(b)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{b.customer_name} <span className="text-slate-400 text-sm ml-1">#{b.booking_number}</span></p>
                      <p className="text-sm text-slate-500">{b.service_name} · {b.time_info || fmtDate(b.booking_date)} · {b.duration_label}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={`text-xs ${STATUS_MAP[b.status]?.color || 'bg-slate-100'}`}>{STATUS_MAP[b.status]?.label || b.status}</Badge>
                      <p className="text-sm font-bold mt-1">{fmt(b.total_price)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* STATS TAB */}
        <TabsContent value="stats">
          {stats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Hari Ini</p><p className="text-2xl font-bold">{stats.today}</p></CardContent></Card>
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Upcoming</p><p className="text-2xl font-bold">{stats.upcoming}</p></CardContent></Card>
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Selesai</p><p className="text-2xl font-bold">{stats.completed}</p></CardContent></Card>
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Dibatalkan</p><p className="text-2xl font-bold">{stats.cancelled}</p></CardContent></Card>
              </div>
              {stats.services?.length > 0 && (
                <Card>
                  <CardHeader className="py-3"><CardTitle className="text-base">Revenue per Layanan</CardTitle></CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {stats.services.map(s => (
                        <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div><p className="font-medium text-sm">{s.name}</p><p className="text-xs text-slate-500">{s.bookings} booking</p></div>
                          <p className="font-bold text-green-600">{fmt(s.revenue)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card><CardContent className="p-12 text-center"><p className="text-slate-500">Memuat statistik...</p></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Service form dialog */}
      <Dialog open={showSvcForm} onOpenChange={setShowSvcForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editSvcId ? 'Edit Layanan' : 'Buat Layanan Baru'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Layanan *</Label><Input value={svcForm.name} onChange={e => setSvcForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Toyota Avanza 2024" /></div>
            <div>
              <Label>Tipe Booking *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {MODES.map(m => (
                  <div
                    key={m.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${svcForm.booking_mode === m.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                    onClick={() => setSvcForm(f => ({ ...f, booking_mode: m.id }))}
                  >
                    <m.icon size={16} className={svcForm.booking_mode === m.id ? 'text-blue-600' : 'text-slate-400'} />
                    <p className="font-medium text-sm mt-1">{m.label}</p>
                    <p className="text-xs text-slate-500">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Harga ({MODES.find(m => m.id === svcForm.booking_mode)?.unit})</Label>
                <Input type="number" min={0} value={svcForm.price} onChange={e => setSvcForm(f => ({ ...f, price: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Kapasitas</Label>
                <Input type="number" min={1} value={svcForm.capacity} onChange={e => setSvcForm(f => ({ ...f, capacity: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            {['hourly', 'session'].includes(svcForm.booking_mode) && (
              <div><Label>Durasi per sesi (menit)</Label><Input type="number" min={1} value={svcForm.duration_minutes} onChange={e => setSvcForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 60 }))} /></div>
            )}
            <div><Label>Deskripsi</Label><Textarea rows={3} value={svcForm.description} onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))} placeholder="Deskripsi layanan..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lokasi</Label><Input value={svcForm.location} onChange={e => setSvcForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Jakarta Selatan" /></div>
              <div><Label>Jam Operasi</Label><Input value={svcForm.operating_hours} onChange={e => setSvcForm(f => ({ ...f, operating_hours: e.target.value }))} placeholder="e.g. 08:00-17:00" /></div>
            </div>
            <div><Label>Kategori</Label><Input value={svcForm.category} onChange={e => setSvcForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Rental Mobil" /></div>
            <div><Label>URL Gambar (pisahkan dengan enter)</Label>
              <Textarea rows={2} value={(svcForm.images || []).join('\n')} onChange={e => setSvcForm(f => ({ ...f, images: e.target.value.split('\n').filter(Boolean) }))} placeholder="https://..." />
            </div>
            <div><Label>Syarat & Ketentuan</Label><Textarea rows={3} value={svcForm.terms} onChange={e => setSvcForm(f => ({ ...f, terms: e.target.value }))} /></div>
            <div><Label>Kebijakan Pembatalan</Label><Textarea rows={2} value={svcForm.cancellation_policy} onChange={e => setSvcForm(f => ({ ...f, cancellation_policy: e.target.value }))} /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="svc-active" checked={svcForm.is_active} onChange={e => setSvcForm(f => ({ ...f, is_active: e.target.checked }))} />
              <Label htmlFor="svc-active">Aktifkan layanan (tampil di storefront)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSvcForm(false)}>Batal</Button>
            <Button onClick={saveSvc} disabled={savingSvc}>
              {savingSvc && <Loader2 size={14} className="animate-spin mr-1" />}
              {editSvcId ? 'Simpan' : 'Buat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BookingDetailModal />
    </div>
  );
}
